// Contract sanitizer for the find-by-features document-script data
// (task 2026-08-31_find-by-features-performance).
//
// The PDF script (scripts-bij.js) consumes three pieces of exporter data
// whose invariants the CMS cannot enforce:
//
// - visibilityOptions: JSON object mapping each PDF filter field name
//   (feature_0, feature_1, ...) to { optionValue: [1-based product display
//   indices to fade] }. Invariants: every attribute key must reference a
//   filter field that exists in the export (productFilterNames), every
//   filter field must have an attribute entry (the PDF handler dereferences
//   mappedHideLogic[field.name] unconditionally and would throw on a pick
//   otherwise), and every product reference must be an integer >= 1 — the
//   display index IS the product's widget/page target, so null or
//   out-of-domain entries mean a CMS product without a rendered target
//   (historic exports really carried `null` here).
// - mappedCompetingModels: JSON object mapping brand name to
//   [{ name, pageIndex }]. Invariant: every model must have a non-empty
//   name and an integer pageIndex >= 0 — pageIndex is the model's page
//   target, and a model whose comparison sheet was not rendered gets a
//   negative index (index_of_sheet returns -1 when the DOM query fails).
//
// Policy (owner decision): NEVER throw, NEVER block the export. Drop every
// entry that fails an invariant, console.warn each drop with enough detail
// to fix the CMS entry, and proceed with the cleaned data. The pure
// functions below return { cleaned, violations } so the strict Jest tier
// can assert on fixtures; sanitizeFindByFeaturesExportData is the
// warn-and-proceed wrapper the hydration path uses.

function isPlainObject(value) {
  return (
    typeof value === "object" && value !== null && !Array.isArray(value)
  );
}

function parseJsonObject(json, subject, violations) {
  if (typeof json !== "string" || json.trim() === "") {
    // Absent data is not a contract violation: the attach conditions in
    // hydrate.js decide what an absent value means.
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(json);
  } catch (error) {
    violations.push({
      subject,
      code: "unparseable",
      message: subject + " is not valid JSON (" + error.message + ")"
    });
    return null;
  }
  if (!isPlainObject(parsed)) {
    violations.push({
      subject,
      code: "not-an-object",
      message: subject + " must be a JSON object, got " + JSON.stringify(parsed).slice(0, 80)
    });
    return null;
  }
  return parsed;
}

// visibilityOptions + productFilterNames cross-check. Returns
// { visibilityOptions, productFilterNames, violations } where the first two
// are the cleaned serialized values in the exact shape hydrate.js inlines.
// When visibilityOptions is absent/empty the pair is returned untouched
// with no violations — absence is legal (non find-by-features exports).
export function sanitizeVisibilityOptions(visibilityOptionsJson, productFilterNamesCsv) {
  const violations = [];
  const filterNames =
    typeof productFilterNamesCsv === "string" && productFilterNamesCsv !== ""
      ? productFilterNamesCsv.split(",")
      : [];

  const parsed = parseJsonObject(visibilityOptionsJson, "visibilityOptions", violations);
  if (parsed === null) {
    if (violations.length === 0)
      return {
        visibilityOptions: visibilityOptionsJson,
        productFilterNames: productFilterNamesCsv,
        violations
      };
    // Unusable data: no filter field can be given working hide logic, so
    // detach them all rather than shipping a script that throws on the
    // first dropdown pick.
    filterNames.forEach((name) => {
      violations.push({
        subject: "productFilterNames",
        code: "filter-without-logic",
        message:
          'filter field "' + name + '" dropped: visibilityOptions is unusable, ' +
          "so the PDF dropdown would have no hide logic (fix the find-by-features " +
          "page attributes in the CMS)"
      });
    });
    return {
      visibilityOptions: "{}",
      productFilterNames: "",
      violations
    };
  }

  const cleaned = {};
  Object.keys(parsed).forEach((fieldName) => {
    if (filterNames.indexOf(fieldName) === -1) {
      violations.push({
        subject: "visibilityOptions",
        code: "attribute-without-field",
        message:
          'attribute "' + fieldName + '" dropped: it references a PDF filter field ' +
          "absent from the export (productFilterNames: " +
          JSON.stringify(filterNames.join(",")) + ") — the find-by-features page in " +
          "the CMS selects more attributes than the exported document renders"
      });
      return;
    }
    const options = parsed[fieldName];
    if (!isPlainObject(options)) {
      violations.push({
        subject: "visibilityOptions",
        code: "attribute-options-invalid",
        message:
          'attribute "' + fieldName + '" dropped: its options must be an object of ' +
          "{ optionValue: [product indices] }, got " + JSON.stringify(options)
      });
      return;
    }
    const cleanedOptions = {};
    Object.keys(options).forEach((optionValue) => {
      const indices = options[optionValue];
      if (!Array.isArray(indices)) {
        violations.push({
          subject: "visibilityOptions",
          code: "option-indices-invalid",
          message:
            'option "' + optionValue + '" of attribute "' + fieldName + '" dropped: ' +
            "expected an array of product indices, got " + JSON.stringify(indices)
        });
        return;
      }
      cleanedOptions[optionValue] = indices.filter((index) => {
        const valid = Number.isInteger(index) && index >= 1;
        if (!valid)
          violations.push({
            subject: "visibilityOptions",
            code: "product-without-target",
            message:
              "product reference " + JSON.stringify(index) + ' dropped from option "' +
              optionValue + '" of attribute "' + fieldName + '": not a 1-based display ' +
              "index, so the product has no widget/page target in the exported document " +
              "(a CMS product on the find-by-features page without a rendered comparison target)"
          });
        return valid;
      });
    });
    cleaned[fieldName] = cleanedOptions;
  });

  const cleanedFilterNames = filterNames.filter((name) => {
    if (isPlainObject(cleaned[name])) return true;
    violations.push({
      subject: "productFilterNames",
      code: "filter-without-logic",
      message:
        'filter field "' + name + '" dropped: visibilityOptions carries no entry for ' +
        "it, so a pick in that PDF dropdown would throw (the find-by-features page in " +
        "the CMS renders a filter its attribute data does not cover)"
    });
    return false;
  });

  return {
    visibilityOptions: JSON.stringify(cleaned),
    productFilterNames: cleanedFilterNames.join(","),
    violations
  };
}

// mappedCompetingModels check. Returns { mappedCompetingModels, violations }
// with the cleaned serialized value. Absent/empty input is returned
// untouched with no violations.
export function sanitizeCompetingModels(mappedCompetingModelsJson) {
  const violations = [];
  const parsed = parseJsonObject(mappedCompetingModelsJson, "mappedCompetingModels", violations);
  if (parsed === null) {
    return {
      mappedCompetingModels: violations.length === 0 ? mappedCompetingModelsJson : "{}",
      violations
    };
  }

  const cleaned = {};
  Object.keys(parsed).forEach((brand) => {
    const models = parsed[brand];
    if (!Array.isArray(models)) {
      violations.push({
        subject: "mappedCompetingModels",
        code: "brand-models-invalid",
        message:
          'brand "' + brand + '" dropped: expected an array of { name, pageIndex } ' +
          "models, got " + JSON.stringify(models)
      });
      return;
    }
    cleaned[brand] = models.filter((model) => {
      if (!isPlainObject(model) || typeof model.name !== "string" || model.name === "") {
        violations.push({
          subject: "mappedCompetingModels",
          code: "model-invalid",
          message:
            "model " + JSON.stringify(model) + ' dropped from brand "' + brand +
            '": every model needs a non-empty name (fix the product model/variant in the CMS)'
        });
        return false;
      }
      if (!Number.isInteger(model.pageIndex) || model.pageIndex < 0) {
        violations.push({
          subject: "mappedCompetingModels",
          code: "product-without-target",
          message:
            'model "' + model.name + '" dropped from brand "' + brand + '": pageIndex ' +
            JSON.stringify(model.pageIndex) + " is not a rendered page target — the " +
            "product is not on any exported comparison sheet (link the product to an " +
            "active comparison page in the CMS, or remove it from the competitor list)"
        });
        return false;
      }
      return true;
    });
  });

  return { mappedCompetingModels: JSON.stringify(cleaned), violations };
}

// The warn-and-proceed tier used by hydrate.js: cleans all three values and
// console.warns every violation. Never throws, never blocks the export.
export function sanitizeFindByFeaturesExportData(additionalData) {
  const data = additionalData || {};
  const visibility = sanitizeVisibilityOptions(
    data.visibilityOptions,
    data.productFilterNames
  );
  const models = sanitizeCompetingModels(data.mappedCompetingModels);

  const violations = visibility.violations.concat(models.violations);
  violations.forEach((violation) => {
    // eslint-disable-next-line no-console
    console.warn(
      "[pdf-export sanitizer] " + violation.subject + ": " + violation.message
    );
  });

  return {
    visibilityOptions: visibility.visibilityOptions,
    productFilterNames: visibility.productFilterNames,
    mappedCompetingModels: models.mappedCompetingModels,
    violations
  };
}
